/**
 * Bill of Materials (BOM) Service
 *
 * Handles all BOM operations including:
 * - BOM CRUD operations
 * - Material requirements calculation
 * - Cost estimation
 * - Where-used analysis
 * - Version management
 */

import httpClient from './httpClient';

const bomService = {
  // ==================== List & Search ====================

  /**
   * Get list of BOMs with filtering and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default 1)
   * @param {number} params.page_size - Items per page (default 20)
   * @param {string} params.search - Search term
   * @param {string} params.status - Filter by status (draft, active, obsolete)
   * @param {string} params.product_id - Filter by finished product
   * @param {boolean} params.include_inactive - Include inactive BOMs
   */
  async getBoms(params = {}) {
    try {
      const response = await httpClient.get('/api/bom', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching BOMs:', error);
      throw error;
    }
  },

  /**
   * Get a single BOM by ID
   * @param {string} bomId - BOM ID or bom_id
   */
  async getBom(bomId) {
    try {
      const response = await httpClient.get(`/api/bom/${bomId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching BOM:', error);
      throw error;
    }
  },

  /**
   * Get all BOMs for a product
   * @param {string} productId - Product ID
   * @param {boolean} includeInactive - Include inactive BOMs
   */
  async getBomsByProduct(productId, includeInactive = false) {
    try {
      const response = await httpClient.get(`/api/bom/by-product/${productId}`, {
        params: { include_inactive: includeInactive }
      });
      return response.data.items || [];
    } catch (error) {
      console.error('Error fetching BOMs by product:', error);
      throw error;
    }
  },

  /**
   * Get the default BOM for a product
   * @param {string} productId - Product ID
   */
  async getDefaultBom(productId) {
    try {
      const response = await httpClient.get(`/api/bom/default/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching default BOM:', error);
      throw error;
    }
  },

  // ==================== CRUD Operations ====================

  /**
   * Create a new BOM
   * @param {Object} bomData - BOM data
   * @param {string} bomData.finished_product_id - Product ID (required)
   * @param {string} bomData.process_template_id - Optional process template ID
   * @param {number} bomData.finished_quantity - Output quantity per BOM
   * @param {Array} bomData.materials - List of materials
   * @param {number} bomData.version - Version number
   * @param {boolean} bomData.is_default - Set as default BOM
   * @param {string} bomData.client_id - Optional client reference
   * @param {string} bomData.status - BOM status (draft, active)
   * @param {string} bomData.notes - Optional notes
   */
  async createBom(bomData) {
    try {
      const response = await httpClient.post('/api/bom', bomData);
      return response.data;
    } catch (error) {
      console.error('Error creating BOM:', error);
      throw error;
    }
  },

  /**
   * Update an existing BOM
   * @param {string} bomId - BOM ID
   * @param {Object} updates - Fields to update
   */
  async updateBom(bomId, updates) {
    try {
      const response = await httpClient.put(`/api/bom/${bomId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating BOM:', error);
      throw error;
    }
  },

  /**
   * Delete (deactivate) a BOM
   * @param {string} bomId - BOM ID
   */
  async deleteBom(bomId) {
    try {
      const response = await httpClient.delete(`/api/bom/${bomId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting BOM:', error);
      throw error;
    }
  },

  // ==================== Version Management ====================

  /**
   * Create a new version of an existing BOM
   * @param {string} bomId - Source BOM ID
   * @param {Object} changes - Optional changes to apply
   */
  async createNewVersion(bomId, changes = {}) {
    try {
      const response = await httpClient.post(`/api/bom/${bomId}/new-version`, changes);
      return response.data;
    } catch (error) {
      console.error('Error creating BOM version:', error);
      throw error;
    }
  },

  // ==================== Material Requirements ====================

  /**
   * Calculate material requirements for a BOM
   * @param {string} bomId - BOM ID
   * @param {number} quantity - Production quantity
   */
  async getMaterialRequirements(bomId, quantity = 1) {
    try {
      const response = await httpClient.get(`/api/bom/${bomId}/material-requirements`, {
        params: { quantity }
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating material requirements:', error);
      throw error;
    }
  },

  /**
   * Calculate material requirements for a product
   * @param {string} productId - Product ID
   * @param {number} quantity - Production quantity
   * @param {string} bomId - Optional specific BOM to use
   */
  async calculateMaterialRequirements(productId, quantity, bomId = null) {
    try {
      const response = await httpClient.post('/api/bom/material-requirements', {
        product_id: productId,
        quantity,
        bom_id: bomId
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating material requirements:', error);
      throw error;
    }
  },

  // ==================== Cost Estimation ====================

  /**
   * Get detailed cost breakdown for a BOM (materials-based)
   * @param {string} bomId - BOM ID
   * @param {number} quantity - Production quantity
   */
  async getCostEstimate(bomId, quantity = 1) {
    try {
      const response = await httpClient.get(`/api/bom/${bomId}/cost-estimate`, {
        params: { quantity }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting BOM cost estimate:', error);
      throw error;
    }
  },

  /**
   * Get full manufacturing cost estimate using bar stock pricing.
   *
   * Uses CostEstimationService to calculate bar stock cost, labor cost,
   * and electricity cost based on product's tip_diameter and total_length.
   * Falls back to BOM materials cost if product lacks dimensions.
   *
   * @param {string} bomId - BOM ID
   * @param {number} quantity - Production quantity
   */
  async getFullCostEstimate(bomId, quantity = 1) {
    try {
      const response = await httpClient.get(`/api/bom/${bomId}/full-cost-estimate`, {
        params: { quantity }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting full cost estimate:', error);
      throw error;
    }
  },

  // ==================== Where-Used Analysis ====================

  /**
   * Find all BOMs that use a specific material
   * @param {string} productId - Material product ID
   */
  async getWhereUsed(productId) {
    try {
      const response = await httpClient.get(`/api/bom/where-used/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting where-used analysis:', error);
      throw error;
    }
  }
};

export default bomService;
